import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {PageNames} from '../../shared/page-names.enum';
import {TvShopEntertainmentPack} from '../../model/tv-shop-entertainment-pack';
import {NavigationService} from '../../services/navigation/navigation.service';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {BaseActions} from '../../shared/base-actions';
import {takeUntil} from 'rxjs/operators';
import {OrderButtonComponent} from '../../order-button/order-button.component';
import {ImageUtils} from '../../shared/utils/image/image-utils';
import {LabelkeyService} from '../../services/labelkey/labelkey.service';
import {TranslateService} from '@ngx-translate/core';
import {TvShopStateService} from '../../services/state/tv-shop-state.service';
import {Order} from '../../model/order';
import {ModalConfig} from '../../model/modal-config';
import {Faq} from '../../model/faq';
import {LabelConstants} from '../../shared/label-constants';
import {
  productTypeRendition,
  ProductTypeRenditionEnum
} from '../../constants/renditions/product-type-rendition.constants';
import {QrCodeTypeEnum} from '../../enums/streaming-bundles/qr-code-type.enum';
import {TvshopConstants} from '../../shared/tvshop-constants';
import {OmapiIdsUtils} from '../../shared/utils/omapi-ids/omapi-ids-utils';
import {UserUtil} from '../../model/customer/customer.util';
import {LanguageService} from '../../services/language/language.service';

@Component({
  selector: 'app-product-detail-intro',
  templateUrl: './product-detail-intro.component.html',
  styleUrls: ['./product-detail-intro.component.less'],
  providers: [LabelkeyService]
})
export class ProductDetailIntroComponent extends BaseActions implements OnInit, AfterViewInit {

  @Input() titleKey: string;
  @Input() descriptionKey: string;
  @Input() subPackDescriptionKey: string;
  @Input() pack: TvShopEntertainmentPack;
  @Input() subPack: TvShopEntertainmentPack;
  @Input() superiorSubscribedPack: TvShopEntertainmentPack;
  @Input() play: boolean;
  @Input() showTitle = false;
  @Input() showFaqs: boolean;
  @Input() showTermsCta: boolean;
  @Input() showQrTile: boolean;
  @Input() horizontalLogo: boolean;
  @Input() isStreamingBundle = false;
  @Input() rendition: productTypeRendition = ProductTypeRenditionEnum.default;

  @Output() emitOrderProduct = new EventEmitter<Object>();
  @Output() emitPlayTrail = new EventEmitter<Object>();
  @Output() emitCloseTrailer = new EventEmitter<Object>();

  defaultRendition = ProductTypeRenditionEnum.default;
  modalConfig = new ModalConfig();
  componentName = PageNames.DETAIL_PAGE_INTRO;
  showTrailerButton = false;
  isTrailerPlaying = false;
  trailerId: string;
  selectedFaq: Faq;
  termsAndConditionsLabel: Faq;
  isSubscribedStreamingPack = false;
  shouldShowPriceBubble = true;
  qrCodeType: QrCodeTypeEnum;

  private origin: string;
  private extraPrams: Params;
  subX = 1;

  subPackOrderButton: OrderButtonComponent;

  @ViewChild('subPackOrderButton') set content(content: OrderButtonComponent) {
    this.subPackOrderButton = content;
  }

  constructor(private navigationService: NavigationService,
              private router: Router,
              private route: ActivatedRoute,
              protected labelkeyService: LabelkeyService,
              private translateService: TranslateService,
              private languageService: LanguageService,
              private stateService: TvShopStateService) {
    super();
  }

  public get backgroundImageUrl(): string {
    return ImageUtils.getBackgroundImageUrl(this.pack.omapiId, 'intro');
  }

  public get logoUrl(): string {
    let rawLogoUrl = ImageUtils.getLogoUrlForOmapiId(this.pack.omapiId);
    if (this.horizontalLogo) {
      rawLogoUrl = rawLogoUrl.replace('.png', '-horizontal.png');
    }
    return rawLogoUrl;
  }

  public get subLogoUrl(): string {
    return ImageUtils.getLogoUrlForOmapiId(this.subPack.omapiId);
  }

  ngOnInit() {
    this.mapParameters();
    this.checkTrailerAvailability();
    this.setSubscribedStreamingPackState();

    this.stateService.salesOrder$.subscribe(saleOrder => {
      if (saleOrder && saleOrder.status === TvshopConstants.SUBMITTING_STATE) {
        this.setSubscribedStreamingPackState();
      }
    });
    this.setCorrectQRCode();
  }

  onEmitOrderProduct(order: Order): void {
    this.emitOrderProduct.emit(order);
  }

  public onBack(): void {
    this.navigationService.resetNavigation();
    this.navigationService.clearNavigation();
    this.routeOnBackPressed();
  }

  checkTrailerAvailability(): void {
    if (this.pack && this.pack.omapiId) {
      this.showTrailerButtonIfTrailerLabelIsAvailable();
    }
  }

  private showTrailerButtonIfTrailerLabelIsAvailable() {
    const trailerLabelKey = this.labelkeyService.getTailerLabelKey(this.pack.omapiId);
    this.translateService.get(trailerLabelKey).subscribe(value => {
      if (value === '') {
        this.showTrailerButton = false;
        return;
      }
      if (trailerLabelKey !== value) {
        this.showTrailerButton = true;
        this.trailerId = value;
      }
    });
  }

  private mapParameters() {
    this.route.queryParams.pipe(takeUntil(this.stopSubscription))
      .subscribe(params => {
        this.origin = params ? params['origin'] : undefined;
        this.extraPrams = params;
      });
  }

  private routeOnBackPressed() {
    if (this.origin) {
      if (this.origin === 'deeplink-direct') {
        window.close();
      } else {
        window.history.back();
      }
    } else {
      this.router.navigate(['/'], {queryParamsHandling: 'merge'});
    }
  }

  openTrailer() {
    this.emitPlayTrail.emit('playing');
    this.isTrailerPlaying = true;
  }

  closeTrailer() {
    this.emitCloseTrailer.emit('closing');
    if (this.isTrailerPlaying) {
      this.isTrailerPlaying = false;
      this.stateService.setIsTrailerPlaying(this.isTrailerPlaying);
    } else {
      this.onBack();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.subPackOrderButton) {
        this.subPackOrderButton.registerOnNavigationService();
      }
    }, 1000);
  }

  openTAndC(omapiId): void {
    const conditionsDescription = this.labelkeyService.getConditionsModalLabelKey(omapiId);
    this.termsAndConditionsLabel = {
      title: LabelConstants.TERMS_AND_CONDITIONS_TITLE,
      description: conditionsDescription
    };
    this.modalConfig.showTAndC = true;
    this.navigationService.setPreviousState();
    this.registerComponent(PageNames.TERMS_AND_CONDITIONS_MODAL);
  }

  openFaqs(faq: Faq): void {
    this.selectedFaq = faq;
    this.modalConfig.showFaq = true;
    this.navigationService.setPreviousState();
    this.registerComponent(PageNames.FAQS_MODAL);
  }

  closeFaqs(): void {
    this.modalConfig.showFaq = false;
    this.navigationService.restorePreviousState();
  }

  closeTCModal(): void {
    this.modalConfig.showTAndC = false;
    this.navigationService.restorePreviousState();
  }

  private registerComponent(pageName: PageNames) {
    this.navigationService.clearNavigation();
    this.navigationService.setCurrentPageComponent(pageName);
  }

  private setSubscribedStreamingPackState() {
    if (
      (this.pack.isStreamingBundle || this.pack.isStreamingStandaloneProduct || this.pack.isGroupPack || this.pack.isStreamzGroup) &&
      this.pack.subscribed) {
      this.isSubscribedStreamingPack = true;
    }
  }

  private setCorrectQRCode() {
    if (UserUtil.isSohoCustomer(OmapiIdsUtils.currentDarwinUserType)) {
      this.qrCodeType = QrCodeTypeEnum.MY_TELENET_BUSINESS;
    } else {
      this.qrCodeType = QrCodeTypeEnum.MY_TELENET_RESIDENTIAL;
    }
  }
}
